const ATTRIBUTION_STORAGE_KEY = "artha_referral_attribution";
const VISITOR_STORAGE_KEY = "artha_referral_visitor";

export type StoredReferralAttribution = {
  attributionToken: string;
  creatorUniqueId: string;
  expiresAt: string;
};

import { setCookie, getCookie, deleteCookie } from "@/lib/cookieUtils";

const isBrowser = () => typeof window !== "undefined";

const makeId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getReferralVisitorId = (): string | undefined => {
  if (!isBrowser()) return undefined;
  const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY) || getCookie(VISITOR_STORAGE_KEY);
  if (existing) {
    // Sync to both if found in one
    window.localStorage.setItem(VISITOR_STORAGE_KEY, existing);
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 2);
    const maxAge = Math.floor((expires.getTime() - Date.now()) / 1000);
    setCookie(VISITOR_STORAGE_KEY, existing, maxAge);
    return existing;
  }
  const visitorId = makeId();
  window.localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 2);
  const maxAge = Math.floor((expires.getTime() - Date.now()) / 1000);
  setCookie(VISITOR_STORAGE_KEY, visitorId, maxAge);
  return visitorId;
};

export const storeReferralAttribution = (value: StoredReferralAttribution) => {
  if (!isBrowser()) return;
  const stringified = JSON.stringify(value);
  // Store in localStorage for the current subdomain
  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, stringified);
  // Also store in cookie for the main domain (arthalink.info) to support multi-domain sharing
  const expires = new Date(value.expiresAt);
  const maxAge = Math.floor((expires.getTime() - Date.now()) / 1000);
  setCookie(ATTRIBUTION_STORAGE_KEY, stringified, maxAge);
};

export const clearReferralAttribution = () => {
  if (!isBrowser()) return;
  window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  deleteCookie(ATTRIBUTION_STORAGE_KEY);
};

export const getStoredReferralAttribution = (): StoredReferralAttribution | null => {
  if (!isBrowser()) return null;
  // Check localStorage first, fallback to cookie if empty (e.g. on a different subdomain)
  const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || getCookie(ATTRIBUTION_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredReferralAttribution;
    if (!parsed.attributionToken || !parsed.expiresAt) {
      clearReferralAttribution();
      return null;
    }
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      clearReferralAttribution();
      return null;
    }
    return parsed;
  } catch {
    clearReferralAttribution();
    return null;
  }
};
